import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  DataTable, DownloadButton, Heatmap, NameLink, Panel, SegmentedControl, SelectMenu, SortHeader, nextSort,
  type DataTableColumn, type HeatmapRow as GridRow, type SortDirection,
} from '../../../../ui-kit';
import type { ECharts } from 'echarts/core';
import type { HeatmapColumn, HeatmapPayload, HeatmapRow } from '../../../../types/plannerDashboard';
import { downloadChartImage } from '../../../../utils/chartDownload';
import { percent, whole } from '../plannerFormat';
import CampaignTrendChart, { trendKey, trendRows } from './CampaignTrendChart';

type View = 'heatmap' | 'table' | 'trend';

interface CampaignHeatmapProps {
  payload: HeatmapPayload | null;
  loading?: boolean;
  error?: string | null;
  onChange: (next: { from?: string; to?: string; minLeads?: number; by?: string }) => void;
  onDownload: (what: 'xlsx' | 'png' | 'svg', order: { key: string; direction: SortDirection } | null) => void;
  downloading?: boolean;
  problem?: string | null;
  /** A campaign's name was clicked: open its funnel hour by hour. */
  onOpenCampaign?: (row: { channel: string; campaign: string }) => void;
}

/**
 * Every campaign against the day of the week, or against each week of the
 * range, shaded by the rate the reader picks — leads to CAS, MQLs to in
 * force, contacted to CAS.
 *
 * By cohort it answers whether a change to a campaign moved its rate (Lisa,
 * 30 Sep 2026). By hour of day — the hour each lead arrived, on Eastern time —
 * it answers which hours a campaign is worth paying for, and a campaign's name
 * opens its whole funnel hour by hour (Thomas, 21 Oct 2026). It opens on the
 * days of the week, whatever the range.
 *
 * The question it answers is marketing's and marketing's: which days is a campaign
 * worth paying for. So the reader builds the rate from both ends rather than
 * being given one, and a cell resting on too few leads is greyed rather than
 * shaded, because a 12% that is three applications out of twenty-five is not
 * a good Sunday — it is not enough to tell.
 */
// The orders that are not a single weekday: how many the campaign started
// with, how many got there, and the blend of the two over the whole range.
const ALL = 'all';
const BASE = 'base';
const HITS = 'hits';

export default function CampaignHeatmap({
  payload, loading, error, onChange, onDownload, downloading, problem, onOpenCampaign,
}: CampaignHeatmapProps) {
  const [view, setView] = React.useState<View>('heatmap');
  // The Trend view's campaign. It stays picked across new dates or steps
  // while it is still listed; otherwise the biggest campaign is shown.
  const [picked, setPicked] = React.useState<string | null>(null);
  const chartRef = React.useRef<ECharts | null>(null);
  const [chartProblem, setChartProblem] = React.useState<string | null>(null);
  // Best-converting first, over the whole range — the sturdiest number in the
  // row. A single day's order is one click away, and the biggest campaigns
  // are under the Campaign heading.
  const [sort, setSort] = React.useState<{ key: string; direction: SortDirection } | null>(
    { key: ALL, direction: 'desc' },
  );

  const stages = payload?.stages ?? [];
  const fromKey = payload?.from ?? '';
  const toKey = payload?.to ?? '';
  const stageIndex = stages.findIndex((s) => s.key === fromKey);
  const laterStages = stageIndex < 0 ? [] : stages.slice(stageIndex + 1);
  const fromStage = stages.find((s) => s.key === fromKey);
  const toStage = stages.find((s) => s.key === toKey);

  const cohorts = !!payload && payload.by !== 'dow';
  const unit = payload?.unit ?? 'day';
  const everything = `All ${unit}s`;
  const columnsShown: HeatmapColumn[] = payload?.columns
    ?? (payload?.weekdays ?? []).map((d) => ({ key: String(d.dow), short_name: d.short_name, name: d.name, partial: false }));
  const headerOf = (c: { short_name: string }) => c.short_name;
  const onSort = (key: string) => setSort((current) => nextSort(current, key, { startDescending: true }));
  const rows = React.useMemo(() => {
    const list = payload?.rows ?? [];
    if (!sort) return list;
    const value = (r: HeatmapRow): number | null => {
      if (sort.key === ALL) return r.rate;
      if (sort.key === BASE) return r.base;
      if (sort.key === HITS) return r.hits;
      const cell = r.cells.find((c) => c.key === sort.key);
      return cell && !cell.thin ? cell.rate : null;
    };
    // A day with too little behind it has no rate to rank, so it waits at the
    // end whichever way the column is sorted rather than posing as a zero.
    return [...list].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      if (x === null && y === null) return b.base - a.base;
      if (x === null) return 1;
      if (y === null) return -1;
      return sort.direction === 'asc' ? x - y : y - x;
    });
  }, [payload, sort]);

  // Both names together, kept apart: names carry hyphens, so "A-B" + "C" and
  // "A" + "B-C" must not meet as one key.
  const rowKey = (r: { channel: string; campaign: string }) => JSON.stringify([r.channel, r.campaign]);
  const openRow = (key: string) => {
    const row = rows.find((r) => rowKey(r) === key);
    if (row && onOpenCampaign) onOpenCampaign({ channel: row.channel, campaign: row.campaign });
  };
  const opensBy = 'by hour of day';

  const gridRows: GridRow[] = rows.map((r) => ({
    key: rowKey(r),
    label: r.campaign,
    meta: r.channel,
    // What the campaign brought in over the whole range, what came of it, and
    // the blend of the two — which is what its days vary around.
    summaries: [
      { key: BASE, value: r.base },
      { key: HITS, value: r.hits },
      {
        key: ALL,
        value: r.rate,
        title: `Every ${unit} together: ${whole(r.hits)} ${toStage?.label ?? ''} of ${whole(r.base)} ${fromStage?.label ?? ''}`,
      },
    ],
    cells: r.cells.map((c) => ({
      key: c.key,
      value: c.rate,
      muted: c.thin,
      title: (
        <Box sx={{ py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
            {c.name} · {r.campaign}
          </Typography>
          <Typography variant="body2" sx={{ display: 'block', fontWeight: 700 }}>
            {c.rate === null ? 'Not enough to tell' : percent(c.rate)}
          </Typography>
          {/* Both ends named, so the line stands on its own: "8 CAS of 235
              MQLs" rather than "8 of 235 MQLs", which leaves the 8 to guess. */}
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
            {whole(c.hits)} {toStage?.label ?? ''} of {whole(c.base)} {fromStage?.label ?? ''}
          </Typography>
        </Box>
      ),
    })),
  }));

  const columns: DataTableColumn<HeatmapRow>[] = [
    // The campaign name stays put while the weekdays scroll past it.
    {
      key: 'campaign',
      header: 'Campaign',
      width: 260,
      sticky: true,
      render: (r) => (
        <Box sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          {onOpenCampaign ? (
            <NameLink onClick={() => openRow(rowKey(r))} aria-label={`${r.campaign}, ${opensBy}`}>
              {r.campaign}
            </NameLink>
          ) : r.campaign}
        </Box>
      ),
    },
    { key: 'channel', header: 'Channel', muted: true, render: (r) => r.channel },
    {
      key: 'base',
      header: <SortHeader label={fromStage?.label ?? 'Leads'} numeric direction={sort?.key === BASE ? sort.direction : null} onClick={() => onSort(BASE)} />,
      numeric: true,
      render: (r: HeatmapRow) => whole(r.base),
    },
    {
      key: 'hits',
      header: <SortHeader label={toStage?.label ?? ''} numeric direction={sort?.key === HITS ? sort.direction : null} onClick={() => onSort(HITS)} />,
      numeric: true,
      render: (r: HeatmapRow) => whole(r.hits),
    },
    {
      key: 'total',
      header: <SortHeader label={everything} numeric direction={sort?.key === ALL ? sort.direction : null} onClick={() => onSort(ALL)} />,
      numeric: true,
      render: (r: HeatmapRow) => (
        <Box component="span" sx={{ fontWeight: 600 }}>{r.rate === null ? '—' : percent(r.rate)}</Box>
      ),
    },
    ...columnsShown.map((d) => ({
      key: `c${d.key}`,
      header: (
        <SortHeader
          label={headerOf(d)}
          numeric
          direction={sort?.key === d.key ? sort.direction : null}
          onClick={() => onSort(d.key)}
        />
      ),
      numeric: true,
      render: (r: HeatmapRow) => {
        const cell = r.cells.find((c) => c.key === d.key);
        if (!cell || cell.base === 0) return <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Box>;
        return (
          <Box component="span" sx={(t) => ({ color: cell.thin ? t.tokens.text.tertiary : undefined })}>
            {cell.rate === null ? '—' : percent(cell.rate)}
            <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary, ml: 0.5 })}>
              ({whole(cell.hits)}/{whole(cell.base)})
            </Box>
          </Box>
        );
      },
    })),
  ];

  const minLeads = payload?.min_leads ?? 10;
  const hidden = payload?.hidden_campaigns ?? 0;
  const counted = fromStage?.label ?? 'leads';

  const trendList = trendRows(payload);
  const trendRow = trendList.find((r) => trendKey(r) === picked) ?? trendList[0] ?? null;
  const trendTitle = `${trendRow?.campaign ?? 'One campaign'}, ${cohorts ? `${unit} by ${unit}` : 'by day of week'}`;
  const downloadChart = (format: 'png' | 'svg') => {
    setChartProblem(null);
    try {
      downloadChartImage(chartRef.current, format, trendTitle);
    } catch (err) {
      setChartProblem(err instanceof Error ? err.message : 'The chart could not be downloaded.');
    }
  };

  const grid = (fill: boolean) =>
    view === 'trend' ? (
      payload && trendRow ? (
        <CampaignTrendChart
          payload={payload}
          row={trendRow}
          fill={fill}
          onReady={fill ? undefined : (instance) => { chartRef.current = instance; }}
        />
      ) : (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          No campaigns with enough leads in this range. Lower the minimum, or widen the dates.
        </Typography>
      )
    ) : view === 'heatmap' ? (
      <Heatmap
        ariaLabel={payload?.title ?? 'Campaigns by day of week'}
        rowLabel="Campaign"
        summaryColumns={[
          { key: BASE, label: fromStage?.label ?? 'Leads', format: (v) => whole(v) },
          { key: HITS, label: toStage?.label ?? '', format: (v) => whole(v) },
          { key: ALL, label: everything },
        ]}
        sort={sort}
        onSort={onSort}
        columns={columnsShown.map((d) => ({ key: d.key, label: headerOf(d) }))}
        rows={gridRows}
        onRowOpen={onOpenCampaign ? openRow : undefined}
        rowOpenLabel={opensBy}
        formatValue={(v) => percent(v)}
        emptyText="No campaigns with enough leads in this range. Lower the minimum, or widen the dates."
      />
    ) : (
      <DataTable
        ariaLabel={payload?.title ?? 'Campaigns by day of week'}
        columns={columns}
        groups={[{ key: 'rows', title: '', rows }]}
        getRowKey={rowKey}
        collapsibleGroups={false}
        maxHeight={fill ? undefined : 560}
        fill={fill}
        emptyMessage="No campaigns with enough leads in this range."
      />
    );

  return (
    <Panel
      title={payload?.title ?? 'Campaigns by day of week'}
      subtitle={
        fromStage && toStage
          ? view === 'trend'
            ? `Of one campaign's ${fromStage.plural} ${cohorts ? `in each ${unit}` : 'on each day'}, the share that reached ${toStage.label}, next to every campaign together.`
            : `Of each campaign's ${fromStage.plural} ${cohorts ? `in that ${unit}` : 'on that day'}, the share that reached ${toStage.label}.`
              + (onOpenCampaign ? ' Click a campaign to see every step hour by hour.' : '')
          : undefined
      }
      tools={
        <>
          <SelectMenu
            label="From"
            value={fromKey}
            options={stages.slice(0, -1).map((s) => ({ value: s.key, label: s.label }))}
            onChange={(v) => onChange({ from: v })}
            disabled={loading || !payload}
          />
          <SelectMenu
            label="to"
            value={toKey}
            options={laterStages.map((s) => ({ value: s.key, label: s.label }))}
            onChange={(v) => onChange({ to: v })}
            disabled={loading || !payload}
          />
          {((payload?.cohort_options?.length ?? 0) > 0 || !!payload?.hours) && (
            <SelectMenu
              // "By", not "Cohort": an hour of the day is not a cohort.
              label="By"
              value={payload?.by ?? 'dow'}
              // Days of the week, the hours of the day, then the cohorts this
              // range offers — the same choices, counts and default as every
              // cohort table.
              options={[
                { value: 'dow', label: 'Day of week', description: 'Mon to Sun, across the whole range' },
                ...(payload?.hours
                  ? [{ value: 'hour', label: 'Hour of day', description: `The hour each lead arrived, in ${payload.hours.label}` }]
                  : []),
                ...(payload?.cohort_options ?? []).map((o) => ({
                  value: o.key,
                  label: o.label,
                  description: `${o.periods} cohorts${o.default ? ' · the default for this range' : ''}`,
                })),
              ]}
              onChange={(v) => {
                // A weekday's column has no match among weeks or hours, so
                // the order goes back to the one the grid opens on.
                setSort({ key: ALL, direction: 'desc' });
                onChange({ by: v });
              }}
              disabled={loading || !payload}
            />
          )}
          <SelectMenu
            label={`Min ${fromStage?.label ?? 'leads'} a ${unit}`}
            value={String(minLeads)}
            valueLabel={minLeads === 0 ? 'No minimum' : String(minLeads)}
            options={(payload?.min_leads_options ?? []).map((n) => ({
              value: String(n),
              label: n === 0 ? 'No minimum' : String(n),
            }))}
            onChange={(v) => onChange({ minLeads: Number(v) })}
            // The listed steps are the common answers, not the only ones, so
            // the list ends in a box: "Use [ 40 ]".
            customEntry={{
              placeholder: 'e.g. 40',
              parse: (typed) => {
                const n = Number(typed.replace(/[^0-9]/g, ''));
                return Number.isInteger(n) && n >= 0 && n <= 100000 ? String(n) : null;
              },
            }}
            disabled={loading || !payload}
          />
          <SegmentedControl
            ariaLabel="View"
            value={view}
            onChange={setView}
            options={[
              { value: 'heatmap', label: 'Heatmap' },
              { value: 'table', label: 'Table' },
              { value: 'trend', label: 'Trend' },
            ]}
          />
          {view === 'trend' && (
            <SelectMenu
              label="Campaign"
              value={trendRow ? trendKey(trendRow) : ''}
              valueLabel={trendRow?.campaign ?? '—'}
              options={trendList.map((r) => ({
                value: trendKey(r),
                label: r.campaign,
                group: r.channel,
                meta: `${whole(r.base)} ${fromStage?.label ?? ''}`,
              }))}
              onChange={setPicked}
              disabled={loading || trendList.length === 0}
            />
          )}
        </>
      }
      actions={
        <>
          <DownloadButton
            formats={[
              {
                key: 'xlsx',
                label: 'Excel (.xlsx)',
                description: `Every campaign by ${cohorts ? unit : 'day of week'}, with the counts behind each rate`,
                onDownload: () => onDownload('xlsx', sort),
              },
              {
                key: 'png',
                label: 'Image (.png)',
                description: view === 'trend' ? 'This campaign\'s chart, to paste into a deck or an email' : 'The grid as it stands, to paste into a deck or an email',
                onDownload: () => (view === 'trend' ? downloadChart('png') : onDownload('png', sort)),
              },
              {
                key: 'svg',
                label: 'Vector image (.svg)',
                description: 'The same picture, sharp at any size',
                onDownload: () => (view === 'trend' ? downloadChart('svg') : onDownload('svg', sort)),
              },
            ]}
            tooltip={view === 'trend' ? 'Download this chart' : 'Download this grid'}
            loading={downloading}
            disabled={loading || !payload}
          />
        </>
      }
      loading={loading && !payload}
      loadingHeight={360}
      flush={view === 'table'}
      // Expanded, the table and the chart take the window's height; the
      // heatmap keeps its own, as tall as its rows.
      expanded={{ content: grid(view !== 'heatmap') }}
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {minLeads === 0
              ? `Every ${unit} with any ${counted} is shaded, however few.`
              : view === 'trend'
                ? `A gap in the line is a ${unit} with fewer than ${minLeads} ${counted}: counted, not rated. Hover a point for the counts behind it.`
                : `${unit.charAt(0).toUpperCase()}${unit.slice(1)}s with fewer than ${minLeads} ${counted} are not shaded.`}
            {hidden > 0
              && ` ${whole(hidden)} campaign${hidden === 1 ? '' : 's'} never reached ${minLeads} ${counted} in any ${unit}, so ${hidden === 1 ? 'it is' : 'they are'} not shown.`}

          </Typography>
          {(problem || chartProblem) && (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem ?? chartProblem}</Typography>
          )}
        </Box>
      }
    >
      {error ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>{error}</Typography>
      ) : (
        grid(false)
      )}
    </Panel>
  );
}
